import {callbackCancelledResult, useSafeAsyncCallback} from '@github-ui/use-safe-async-callback'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback} from 'react'

import {MemexColumnDataType} from '../api/columns/contracts/memex-column'
import type {UpdateColumnValueAction} from '../api/columns/contracts/storage'
import type {UpdateItemContext} from '../api/memex/contracts'
import {ItemType} from '../api/memex-items/item-type'
import {ItemOrder} from '../api/stats/contracts'
import {getColumnText} from '../components/column-detail-helpers'
import useToasts from '../components/toasts/use-toasts'
import {DropSide} from '../helpers/dnd-kit/drop-helpers'
import {getIssueFieldPasteErrorMessage} from '../helpers/issue-field-value-helpers'
import type {RequireAtLeastOne} from '../helpers/type-utilities'
import {buildUpdateMemexItemActions, type MemexItemModel} from '../models/memex-item-model'
import {buildRevertForUpdate} from '../state-providers/column-values/column-value'
import {useUpdateItemColumnValue} from '../state-providers/column-values/use-update-item-column-value'
import {useHistory} from '../state-providers/history/history'
import {isQueryKeyForGroupedItems} from '../state-providers/memex-items/queries/query-keys'
import {
  findMemexItemGlobalIndexByIdInQueryClient,
  getMemexItemModelsFromQueryClient,
  getQueryKeysForItem,
  type OptimisticUpdateRollbackData,
  rollbackMemexItemData,
} from '../state-providers/memex-items/query-client-api/memex-items'
import type {ReorderItemData} from '../state-providers/memex-items/types'
import {useFindMemexItem} from '../state-providers/memex-items/use-find-memex-item'
import {useSetNewRowPosition} from '../state-providers/memex-items/use-set-new-row-position'
import {HistoryResources, Resources} from '../strings'
import {usePostStats} from './common/use-post-stats'

export type UpdateItemActions = RequireAtLeastOne<
  {reorderData: ReorderItemData} & {updateColumnActions: Array<UpdateColumnValueAction>}
>

/**
 * Provides a function to update a single column in an item.
 */
export function useUpdateItem() {
  const {updateColumnValueAndPriority} = useUpdateItemColumnValue()
  const safeUpdateColumnValueAndPriority = useSafeAsyncCallback(updateColumnValueAndPriority, true)
  const {addToast} = useToasts()
  const {findMemexItem} = useFindMemexItem()
  const safeFindMemexItem = useSafeAsyncCallback(findMemexItem, true)

  const history = useHistory()

  /**
   * @param sourceRepoId If provided, will force labels, assignees, & milestones to be applied within the same repository. This is
   * stricter than the default behavior, which will attempt to search across repositories for matching values.
   */
  const updateItem = useCallback(
    async (
      item: MemexItemModel,
      update: UpdateColumnValueAction,
      description = HistoryResources.updateItem,
      sourceRepoId?: number | null,
    ) => {
      if (!item) {
        return Promise.resolve()
      }

      const isUpdatingIssueField = 'isIssueField' in update ? update.isIssueField : false

      if (sourceRepoId !== undefined || isUpdatingIssueField) {
        try {
          validateUpdate([item], update, sourceRepoId ?? null)
        } catch (error) {
          if (error instanceof UpdateValidationError) {
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              message: error.message,
              type: 'warning',
            })
            return
          } else {
            throw error
          }
        }
      }

      const revertUpdate = buildRevertForUpdate(update, item)

      const loadedFieldIds = item.memexProjectColumnValues.map(c => c.memexProjectColumnId)
      await safeUpdateColumnValueAndPriority(
        item,
        {columnValues: [update]},
        {fieldIds: loadedFieldIds, showErrorToast: isUpdatingIssueField},
      )

      history?.registerAction({
        description,
        revert: async () => {
          // It isn't safe to reuse `item` now that it has been updated
          const latestItem = safeFindMemexItem(item.id)
          if (typeof latestItem === 'object')
            await safeUpdateColumnValueAndPriority(latestItem, {columnValues: [revertUpdate]}, {})
        },
      })
    },
    [safeUpdateColumnValueAndPriority, safeFindMemexItem, addToast, history],
  )

  return {updateItem}
}

/**
 * This hook provides a function - updateAndReorderItem - which can be called to update the display order of a memex item
 * and / or a column value for the item
 */
export function useUpdateAndReorderItem() {
  const {postStats} = usePostStats()
  const {setNewRowPosition} = useSetNewRowPosition()
  const {findMemexItem} = useFindMemexItem()
  const {updateColumnValueAndPriority} = useUpdateItemColumnValue()
  const queryClient = useQueryClient()
  const history = useHistory()

  const performUpdate = useCallback(
    async (
      item: MemexItemModel,
      {reorderData, updateColumnActions}: UpdateItemActions,
      {layoutType, parentIssueId}: UpdateItemContext = {},
    ) => {
      let previousItemId: number | '' | undefined = undefined
      let rollbackData: OptimisticUpdateRollbackData | undefined = undefined

      if (reorderData) {
        const result = setNewRowPosition(item.id, reorderData)
        previousItemId = result?.previousItemId
        rollbackData = result?.rollbackData
      }
      const updateMemexItemActions = buildUpdateMemexItemActions(updateColumnActions, previousItemId)
      if (!updateMemexItemActions) {
        return
      }

      try {
        await updateColumnValueAndPriority(item, updateMemexItemActions, {layoutType, parentIssueId})
      } catch (error) {
        // revert the change to the item's priority and continue error propagation
        if (rollbackData) {
          rollbackMemexItemData(queryClient, rollbackData)
        }
        throw error
      }

      const newIndexForRow = findMemexItemGlobalIndexByIdInQueryClient(queryClient, item.id)
      postStats({
        name: ItemOrder,
        context: `position: ${newIndexForRow}`,
        memexProjectItemId: item.id,
      })
    },
    [postStats, queryClient, setNewRowPosition, updateColumnValueAndPriority],
  )

  const safePerformUpdate = useSafeAsyncCallback(performUpdate, true)
  const safeFindMemexItem = useSafeAsyncCallback(findMemexItem, true)

  const buildRevertForReorder = useCallback(
    (item: MemexItemModel): ReorderItemData | undefined => {
      const allItems = getMemexItemModelsFromQueryClient(queryClient)

      const currentIndex = allItems.findIndex(i => i.id === item.id)
      const currentPrevItem = allItems[currentIndex - 1]
      const referenceItem = currentPrevItem ? currentPrevItem : allItems[0]
      if (!referenceItem) return undefined
      const referenceSide = currentPrevItem ? DropSide.AFTER : DropSide.BEFORE
      const reorderData: ReorderItemData = {overItemId: referenceItem.id, side: referenceSide, overGroupId: undefined}
      const referenceItemKeys = getQueryKeysForItem(queryClient, referenceItem.id)

      // If the reference item is in a group, we need to capture the group id so we can revert properly
      if (referenceItemKeys && referenceItemKeys[0] && isQueryKeyForGroupedItems(referenceItemKeys[0])) {
        reorderData.overGroupId = referenceItemKeys[0][3].groupId
        reorderData.overSecondaryGroupId = referenceItemKeys[0][3].secondaryGroupId
      }
      return reorderData
    },
    [queryClient],
  )

  const updateAndReorderItem = useCallback(
    async (id: number, actions: UpdateItemActions, context: UpdateItemContext = {}) => {
      const item = findMemexItem(id)
      if (!item) return

      const revertColumnActions = actions.updateColumnActions?.map(action => buildRevertForUpdate(action, item))
      const revertReorder = buildRevertForReorder(item)

      // Everything inside this revert method must be 'safe' or it could be outdated by the time the user undos
      const revert = async () => {
        // It's not safe to reuse the previously found item, since it will have been updated now
        const updatedItem = safeFindMemexItem(id)
        if (!updatedItem || updatedItem === callbackCancelledResult) return

        if (revertColumnActions || revertReorder)
          await safePerformUpdate(
            updatedItem,
            {updateColumnActions: revertColumnActions, reorderData: revertReorder} as UpdateItemActions,
            {},
          )
      }

      await performUpdate(item, actions, context)

      history?.registerAction({
        description: HistoryResources.moveItem,
        revert,
      })
    },
    [findMemexItem, buildRevertForReorder, performUpdate, history, safeFindMemexItem, safePerformUpdate],
  )

  return {updateAndReorderItem}
}

export function buildUpdateItemActions(
  updateColumnActions: Array<UpdateColumnValueAction> | undefined,
  reorderData: ReorderItemData | undefined,
): UpdateItemActions | undefined {
  if (updateColumnActions?.length && reorderData) {
    return {reorderData, updateColumnActions}
  }

  if (updateColumnActions?.length) {
    return {updateColumnActions}
  }

  if (reorderData) {
    return {reorderData}
  }

  // Ideally, we'd be able to build this item without possibly returning `undefined`
  // as that is not a valid case. However, at all of the callsites of updateAndReorderItem
  // we don't know for sure which (or both) of the two pieces of data is defined, so
  // we need to use a run-time check to validate
  return undefined
}

export class UpdateValidationError extends Error {
  constructor(...args: ConstructorParameters<typeof Error>) {
    super(...args)
    this.name = 'UpdateValidationError'
  }
}

/**
 * Attempt to validate the update on the client side to avoid an unecessary round trip if we know it will fail.
 * @throws {UpdateValidationError} if the update is known not to be valid on the given items.
 */
export function validateUpdate(
  items: Array<MemexItemModel>,
  update: UpdateColumnValueAction,
  sourceRepoId: number | null,
): void {
  const isIssueFieldColumn = 'isIssueField' in update ? update.isIssueField : false

  if (isIssueFieldColumn) {
    for (const item of items) {
      const validationError = getIssueFieldPasteErrorMessage(item.contentType, item.getOwner() ?? '')
      if (validationError) {
        throw new UpdateValidationError(validationError)
      }
    }
    return
  }

  switch (update.dataType) {
    case MemexColumnDataType.Assignees:
      for (const item of items)
        if (
          (item.contentType === ItemType.Issue || item.contentType === ItemType.PullRequest) &&
          sourceRepoId !== item.contentRepositoryId
        )
          throw new UpdateValidationError(Resources.unableToPasteAssigneesBetweensRepos)

      break

    case MemexColumnDataType.Milestone:
      for (const item of items) {
        if (item.contentType === ItemType.DraftIssue)
          throw new UpdateValidationError(Resources.unableToSetColumnOnDraftIssue(getColumnText(update.dataType)))
        if (sourceRepoId !== item.contentRepositoryId)
          throw new UpdateValidationError(Resources.unableToPasteMilestonesBetweensRepos)
      }
      break

    case MemexColumnDataType.IssueType:
      for (const item of items) {
        if (item.contentType === ItemType.DraftIssue)
          throw new UpdateValidationError(Resources.unableToSetColumnOnDraftIssue(Resources.issueType))
        if (item.contentType === ItemType.PullRequest)
          throw new UpdateValidationError(Resources.unableToSetColumnOnPullRequest(Resources.issueType))
      }
      break

    case MemexColumnDataType.ParentIssue:
      for (const item of items) {
        if (item.contentType === ItemType.DraftIssue)
          throw new UpdateValidationError(Resources.unableToSetColumnOnDraftIssue(getColumnText(update.dataType)))
        if (item.contentType === ItemType.PullRequest)
          throw new UpdateValidationError(Resources.unableToSetColumnOnPullRequest(getColumnText(update.dataType)))
      }
      break

    case MemexColumnDataType.Labels:
      for (const item of items) {
        if (item.contentType === ItemType.DraftIssue)
          throw new UpdateValidationError(Resources.unableToSetColumnOnDraftIssue(getColumnText(update.dataType)))
        if (sourceRepoId !== item.contentRepositoryId)
          throw new UpdateValidationError(Resources.unableToPasteLabelsBetweensRepos)
      }
  }
}
